// Cart drawer. Opens on the header cart icon (purolabs:cart:open), Shopify.actions.openCart, and whenever something
// is added to the cart. Line changes, errors, status messages and re-rendering are shared with the cart page
// (CartView in assets/purolabs-cart.js). The recommendations under the lines (a panel beside the drawer on desktop)
// keep what they loaded between re-renders and drop products that are now in the cart.
import { CartView, Recommendations, addToCart } from '@purolabs/cart';
import { openDialog, setupDialog } from '@purolabs/dialog';

class CartDrawer extends CartView {
  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.abortController;
    this.dialog = this.querySelector('dialog');
    setupDialog(this.dialog, signal);

    document.addEventListener('purolabs:cart:open', () => this.open(), { signal });

    this.dialog.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('[data-close-cart]')) this.dialog.close();
      },
      { signal }
    );

    this.dialog.addEventListener(
      'submit',
      (event) => {
        const form = event.target.closest('[data-rec-form]');
        if (!form) return;
        event.preventDefault();
        this.#addRecommendation(form);
      },
      { signal }
    );
  }

  open() {
    openDialog(this.dialog);
  }

  // Something was added elsewhere on the page: open, with the reason when it failed (unless the form shows it)
  cartChanged(event, result) {
    if (event.action !== 'add' || this.contains(event.target)) return;
    const { detail = {} } = result;
    if (detail.didError) {
      if (detail.handled || !detail.error) return;
      this.#showError(detail.error);
    } else {
      this.#showError('');
    }
    this.open();
  }

  updated() {
    const recs = this.querySelector('pl-cart-recommendations');
    if (recs?.loaded) recs.refresh();
  }

  #showError(message) {
    const error = this.querySelector('[data-cart-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  async #addRecommendation(form) {
    const button = form.querySelector('button');
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    this.#showError('');
    try {
      await addToCart(this, new FormData(form), { source: 'pl-cart-drawer', context: 'cart' });
    } catch (error) {
      if (error.name !== 'AbortError') this.#showError(error.message);
    } finally {
      if (button.isConnected) button.removeAttribute('aria-busy');
    }
  }
}

// Kept between re-renders (the element carries data-skip-subtree-update); refetched when the first product in the
// cart changes or the five-minute cache runs out, and filtered against what's in the cart on every render.
class CartRecommendations extends Recommendations {
  static observedAttributes = ['data-url'];

  connectedCallback() {
    super.connectedCallback();
    // Compact drawer: arrows scroll the row by a card; each is disabled at its end, and both hide when it all fits
    const { signal } = this.abortController;
    this.addEventListener(
      'click',
      (event) => {
        const arrow = event.target.closest('[data-recs-step]');
        const list = this.querySelector('[data-recs-list]');
        const card = list?.querySelector('.pl-cart__rec');
        if (!arrow || !card) return;
        const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
        list.scrollBy({ left: Number(arrow.dataset.recsStep) * (card.offsetWidth + gap), behavior: 'smooth' });
      },
      { signal }
    );
    this.querySelector('[data-recs-list]')?.addEventListener('scroll', () => this.#updateArrows(), { passive: true, signal });
    window.addEventListener('resize', () => this.#updateArrows(), { signal });
  }

  #updateArrows() {
    const nav = this.querySelector('[data-recs-nav]');
    const list = this.querySelector('[data-recs-list]');
    if (!nav || !list) return;
    const end = list.scrollWidth - list.clientWidth;
    nav.hidden = end <= 1;
    const [previous, next] = nav.querySelectorAll('[data-recs-step]');
    previous.disabled = list.scrollLeft <= 1;
    next.disabled = list.scrollLeft >= end - 1;
  }

  attributeChangedCallback(name, previous, next) {
    if (this.loaded && previous !== next) this.load();
  }

  refresh() {
    this.load();
  }

  show(nodes) {
    const inCart = new Set(
      [...document.querySelectorAll('pl-cart-drawer [data-line-key] a.pl-cart__line-title[href]')].map((link) => new URL(link.href).pathname)
    );
    const cards = nodes.filter((node) => node instanceof Element && node.matches('.pl-cart__rec'));
    const list = this.querySelector('[data-recs-list]');
    list.replaceChildren(...this.#leadWithGoal(cards.filter((card) => !inCart.has(new URL(card.querySelector('a').href).pathname))));
    list.scrollLeft = 0;
    // After the hidden attribute comes off (the row has no width until then)
    requestAnimationFrame(() => this.#updateArrows());
    return list.children.length > 0;
  }

  /**
   * Compact drawer: the section gives the nearest goal (data-goal-gap in pence, data-goal-title such as "Add one to
   * get Biotin Complex free"). Products that reach it on their own go first, cheapest first, under that heading;
   * otherwise the usual heading. A product counts only if it still reaches the goal 15% off, in case adding it brings
   * the mixed-cart discount in.
   */
  #leadWithGoal(cards) {
    const title = this.querySelector('[data-recs-title]');
    const gap = Number(this.dataset.goalGap);
    if (!title || !('defaultTitle' in this.dataset)) return cards;
    const reaches = (card) => gap > 0 && Number(card.dataset.price) * 0.85 >= gap;
    const leading = cards.filter(reaches).sort((a, b) => Number(a.dataset.price) - Number(b.dataset.price));
    title.textContent = leading.length && this.dataset.goalTitle ? this.dataset.goalTitle : this.dataset.defaultTitle;
    return [...leading, ...cards.filter((card) => !reaches(card))];
  }

  extract(html) {
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    return section ? [...section.children] : [];
  }
}

if (!customElements.get('pl-cart-drawer')) customElements.define('pl-cart-drawer', CartDrawer);
if (!customElements.get('pl-cart-recommendations')) customElements.define('pl-cart-recommendations', CartRecommendations);
